'use client';

import { AnchorHTMLAttributes, FC, MouseEvent, ReactNode } from 'react';

export interface ShallowLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
   children?: ReactNode;
}

/**
 * A drop-in replacement for a native `<a>` element that keeps the `href`
 * attribute in the DOM (for SEO crawlability and browser hover tooltips) but
 * suppresses the default browser navigation when the element is clicked.
 *
 * This is useful when you want to pair a navigable `href` with a client-side
 * interaction handler (e.g. the `onTabChange` callback of a `<Tabs>` component)
 * without triggering a full or client-side page transition.
 *
 * Pass it as the `linkComponent` prop on components that accept a custom link
 * renderer, such as `<Tab>` or `<Tabs>`:
 *
 * @example
 * ```tsx
 * <Tabs tabs={tabs} linkComponent={ShallowLink} />
 * ```
 *
 * Individual `TabProps` entries can also use it directly:
 *
 * @example
 * ```tsx
 * const tabs: TabProps[] = [
 *   { label: 'Products', href: '?tab=product', linkComponent: ShallowLink },
 * ];
 * ```
 */
export const ShallowLink: FC<ShallowLinkProps> = ({ onClick, children, ...props }) => {
   const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      onClick?.(e);
   };

   return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
      <a {...props} onClick={handleClick}>
         {children}
      </a>
   );
};
